import React, { useState, useMemo } from 'react';
import { IPlace, Button, DateTimeInput, Input, InputContainer, PlaceInput, Maps, fromDateTime, getGMT } from '../../../libs';
import dayjs from 'dayjs';
import localeRu from 'dayjs/locale/ru';
import * as EmailValidator from 'email-validator';
import styled, { css } from 'styled-components';
import { mainApi, postAgreement, s3urls } from '../../../helpers/api';
import { LocationIcon } from '../assets/icons/system';
import { Error } from '../helpers/errors';
import FooterWithLegalDocs from '../../../components/FooterWithLegalDocs';


interface FormProps {
  token: string;
  partner: string;
  setError(value: Error): void;
}

export default function Form(props: FormProps) {
  const defaultDate = dayjs().toISOString();
  const defaultTime = dayjs().utc().set('hour', 12).set('minute', 0).set('second', 0).toISOString();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [date, setDate] = useState<string>(defaultDate);
  const [time, setTime] = useState<string>(defaultTime);
  const [place, setPlace] = useState<IPlace>({name: '', lat: 0, lon: 0});
  const [dtString, setDtString] = useState<string>('');

  const [showMap, setShowMap] = useState<boolean>(false);

  const [nameMessage, setNameMessage] = useState<string | undefined>();
  const [emailMessage, setEmailMessage] = useState<string | undefined>();
  const [timeMessage, setTimeMessage] = useState<string | undefined>();
  const [dateMessage, setDateMessage] = useState<string | undefined>();
  const [placeMessage, setPlaceMessage] = useState<string | undefined>();
  const [isAgree, setIsAgree] = useState<boolean>(false);
  const [isAgreeMailing, setIsAgreeMailing] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  // const [mapUrl, setMapUrl] = useState<string>();

  const isDisabled = useMemo((): boolean => {
    return Boolean(
      nameMessage ||
        !name ||
        emailMessage ||
        !email ||
        timeMessage ||
        !time ||
        dateMessage ||
        !date ||
        placeMessage ||
        !place.name
    );
  }, [nameMessage, name, emailMessage, email, timeMessage, time, dateMessage, date, placeMessage, place]);

  const onNameChange = (name: string) => {
    const resultName = name.trim();
    setName(name);
    if (/^[a-z,а-я,ё,\s]*$/i.test(resultName)) {
      setNameMessage(resultName.length === 0 ? 'Имя не может быть пустым' : undefined);
    } else {
      setNameMessage('В имени могут быть только буквы');
    }
  };

  const onEmailChange = (email: string) => {
    setEmail(email);
    setEmailMessage(EmailValidator.validate(email) || email === '' ? undefined : 'Введите корректный email');
  };

  const onDateChange = (isoDate?: string) => {
    const message = 'Нужно ввести дату';
    if (isoDate) {
      setDate(isoDate);
      setDateMessage(
        dayjs(isoDate) < dayjs('1900-01-01')
          ? 'Введите корректную дату'
          : dayjs(isoDate) > dayjs()
            ? 'Введите дату в прошлом'
            : undefined,
      );
    } else {
      setDateMessage(message);
    }
  };

  const onTimeChange = (isoTime?: string) => {
    const message = 'Нужно ввести время';

    if (isoTime) {
      setTime(isoTime);
      setTimeMessage(undefined);
    } else {
      setTimeMessage(message);
    }
  };

  const onPlaceChange = (place: string) => {
    setPlace((place) => ({ ...place, lat: 0, lon: 0 }));
    setPlaceMessage('Выберите город из списка');
  };

  const onPlaceSelect = (place: IPlace | null) => {
    setPlace(place as IPlace);
    setPlaceMessage(undefined);
  }

  const checkErrorMode = (val: any) => (Boolean(val) ? 'error' : 'normal');

  const handleSubmit = async () => {
    if(!isAgree) {
      setError(true)
      return;
    }

    await postAgreement(isAgree, isAgreeMailing, email);
    const formatedDate = dayjs.utc(date).format('DD.MM.YYYY');
    const formatedTime = dayjs.utc(time).format('HH:mm:ss');
    setDtString(fromDateTime(formatedDate, formatedTime));
    setShowMap(true);
  };


  if (showMap) {
    const birthDate = dayjs.utc(date).locale(localeRu).format('DD MMMM YYYY');
    const birthTime = dayjs.utc(time).format('HH:mm:ss');
    return (
      <Container className='map'>
        <h3>Ваша натальная карта</h3>
        <h5>{birthDate}, {birthTime}, {place.name}</h5>
        <StyledMap
          modes={['natal']}
          form={{
            // @ts-ignore
            natal: {
              // @ts-ignore
              dateTime: {date, time},
              place: place,
              dt: dtString,
            },
          }}
          settings={{
            access: { limited: true },
            diameter: 450,
            theme: 'light',
          }}
        />
      </Container>
    );
  }

  return (
    <Container>
      <Description>
        <div className={'title'}>Постройте вашу натальную карту</div>
        <div className={'subtitle'}>Заполните форму и получите вашу карту</div>
      </Description>

      <FormContainer>
        <InputContainer label="Имя" message={nameMessage}>
          <StyledInput
            name="username"
            size="small"
            value={name}
            onChange={onNameChange}
            placeholder="Как Вас зовут?"
            mode={checkErrorMode(nameMessage)}
          />
        </InputContainer>

        <InputContainer label="Почта" message={emailMessage}>
          <StyledInput
            name="email"
            size="small"
            value={email}
            onChange={onEmailChange}
            mode={checkErrorMode(emailMessage)}
            placeholder="Ваш e-mail"
          />
        </InputContainer>

        <InputContainer label="Дата рождения" message={dateMessage}>
          <StyledDateTimeInput
            size="small"
            type="date"
            value={date}
            onChange={onDateChange}
            onInvalid={onDateChange}
            hideIcon={true}
            mode={checkErrorMode(dateMessage)}
            utcMode
          />
        </InputContainer>

        <InputContainer label="Время рождения" message={timeMessage}>
          <StyledDateTimeInput
            size="small"
            type="time"
            value={time}
            hideIcon={true}
            onChange={onTimeChange}
            onInvalid={onTimeChange}
            mode={checkErrorMode(timeMessage)}
            utcMode
          />
        </InputContainer>

        <InputContainer label="Место рождения" message={placeMessage}>
          <StyledPlaceInput
            size="small"
            value={place?.name || ''}
            onSelect={onPlaceSelect}
            onChange={onPlaceChange}
            placeholder="Начните вводить место рождения"
            asyncDataFn={mainApi.places.bind(mainApi)}
            asyncDetailFn={mainApi.place.bind(mainApi)}
            mode={checkErrorMode(placeMessage)}
            lang="ru"
            rightIcon={<LocationIcon/>}
          />
        </InputContainer>

        <StyledButton name="submit-button" color="blue" onClick={handleSubmit} disabled={isDisabled}>
          Построить
        </StyledButton>
      </FormContainer>
      <Footer>
        <FooterWithLegalDocs 
          isAgree={isAgree} 
          isAgreeMailing={isAgreeMailing} 
          setIsAgree={setIsAgree} 
          setIsAgreeMailing={setIsAgreeMailing} 
          showOferta={true} 
          showAgree={true} 
          showAgreeMailing={true}
          error={error ? 'Примите соглашения' : ''}/>
      </Footer>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  height: 100vh;

  &.map {
    padding: 0 1rem;
  }

  h3 {
    color: var(--text-secondary);
    font-weight: normal;
  }

  h5 {
    margin: 0;
    color: var(--text-third);
    font-weight: normal;
  }

  a {
    text-decoration: none;
    color: #4093f4;
  }

  a:hover {
    text-decoration: underline;
  }

  @media (max-height: 700px) {
    height: 100%;
  }
`;

const Description = styled.div`
  width: 42.5rem;
  max-width: 85%;
  margin-top: 1rem;
  text-align: left;

  .title {
    font-size: 1.25rem;
    font-weight: 500;
    margin-bottom: 0.75rem;
  }

  .subtitle {
    color: var(--text-third);
  }
`;

const FormContainer = styled.div`
  width: 42.5rem;
  max-width: 85%;
  font-size: 0.875rem;
  margin: 2rem 0 1rem;

  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.25rem;

  & > :nth-child(5),
  & > :nth-child(n + 6) {
    grid-column: 1 / 3;
  }

  @media (max-width: 768px) {
    & > :nth-child(1) {
      grid-column: 1 / 3;
    }

    & > :nth-child(2) {
      grid-column: 1 / 3;
    }
  }
`;

const StyledInput = styled(Input)<{ error?: boolean }>`
  ${(props) =>
    props.error &&
    css`
      input {
        border-color: var(--red-color);
      }
    `};

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const StyledDateTimeInput = styled(DateTimeInput)<{ error?: boolean }>`
  ${(props) =>
    props.error &&
    css`
      border-color: var(--red-color);
    `};

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const StyledPlaceInput = styled(PlaceInput)<{ error?: boolean }>`
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const StyledButton = styled(Button)`
  color: white !important;
  background: #4093f4 !important;
  font-size: 1.25rem;

  &[disabled] {
    opacity: 0.3;
  }
`;

const Footer = styled.div`
  width: 42.5rem;
  max-width: 85%;
  display: flex;
  flex-direction: column;
  align-items: center;
  color: var(--gray-color);
  font-size: 1rem;
  margin-bottom: 4rem;

  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

export const StyledMap = styled(Maps)`
  margin: 5rem 0 5rem;

  @media (max-width: 768px) {
    & {
      width: 83% !important;
      height: auto !important;
      /* margin: 3rem auto 4rem; */
    }
      .maps-container > svg {
        width: initial !important;
        height: initial !important;
      }
  }

  svg {
    overflow: visible;
  }

  /* @media print {
    .maps-container > svg {
        width: 450px !important;
        height: 450px !important;
      }
  } */
`;
